
import Vue from 'vue';

import * as BLTypes from '@/types/blacklabtypes';

import { HitContext as ContextOfHit, TokenHighlight } from '@/types/apptypes';
import HitContext from '@/pages/search/results/table/HitContext.vue';
import { ColumnDefs, DisplaySettingsForRendering, HitRowData, snippetParts } from './table-layout';
import DepTree from '@/pages/search/results/table/DepTree.vue';
import Spinner from '@/components/Spinner.vue';

import * as UIStore from '@/store/search/ui';
import * as CorpusStore from '@/store/search/corpus';
import * as Api from '@/api';
import { debugLog } from '@/utils/debug';

/** TODO disconnect from the store? */
export default Vue.component('HitRowDetails', {
	components: {
		HitContext,
		DepTree,
		Spinner
	},
	props: {
		row: Object as () => HitRowData,
		cols: Object as () => ColumnDefs,
		info: Object as () => DisplaySettingsForRendering,

		// data: Object as () => HitRowData,

		// mainAnnotation: Object as () => NormalizedAnnotation,
		// detailedAnnotations: Array as () => NormalizedAnnotation[]|undefined,
		// /** What properties/annotations to show for tokens in the deptree, e.g. lemma, pos, etc. */
		// depTreeAnnotations: Object as () => Record<'lemma'|'upos'|'xpos'|'feats', NormalizedAnnotation|null>,

		// html: Boolean,
		colspan: Number,
		// dir: String as () => 'ltr'|'rtl',

		open: Boolean,

		// // which match infos (capture/relation) should be highlighted because we're hovering over a token? (parallel corpora)
		hoverMatchInfos: {
			type: Array as () => string[],
			default: () => [],
		},
	},
	data: () => ({
		sentenceRequest: null as null|Promise<any>,
		sentence: null as null|BLTypes.BLHit,

		snippetRequest: null as null|Promise<void>,
		snippet: null as null|ContextOfHit,

		error: null as null|string,
		addons: [] as Array<ReturnType<UIStore.ModuleRootState['results']['hits']['addons'][number]>>,

		// whether full sentence is shown (instead of just n words before and after the hit)
		// For this to be available, the sentenceElement must be set (in the ui store)
		sentenceShown: false,
	}),
	computed: {
		hasRelations: CorpusStore.get.hasRelations,
		/** Exact surrounding sentence can only be loaded if we the start location of the current hit, and when the boundery element has been set. */
		sentenceAvailable(): boolean { return this.hasRelations && !!UIStore.getState().search.shared.within.sentenceElement && 'start' in this.row.hit; },

	},
	methods: {
		/**
		 * Separate from the snippet/context, as that can run over sentence boundaries, but this doesn't.
		 * We use it to render the dependency tree for the entire sentence.
		 */
		loadSentence() {
			// 'start' should always be true if this.sentenceAvailable is true, but typescript doesn't know this.
			if (!this.sentenceAvailable || this.sentenceRequest || !('start' in this.row.hit)) return;

			const context = UIStore.getState().search.shared.within.sentenceElement;
			if (!context) return; // unavailable.

			const formatError = UIStore.getState().global.errorMessage;

			const nonce = this.row.hit;
			this.sentenceRequest = Api.blacklab.getSnippet(
				INDEX_ID,
				this.row.doc.docPid,
				this.row.annotatedField?.id,
				this.row.hit.start,
				this.row.hit.end,
				context
			)
			// check if hit hasn't changed in the meantime (due to component reuse)
			.then(r => { if (nonce === this.row.hit) { this.sentence = r; this.sentenceRequest = null; }})
			.catch(e => { if (nonce === this.row.hit) { this.error = formatError(e, 'snippet'); this.sentenceRequest = null; }})
		},
		loadSnippet() {
			// If we don't have a fat hit, we can't get any larger context (because we don't know the start/end of the hit)
			// Don't do anything else, we just won't render the larger context.
			// The small table will still be shown.
			if (this.snippetRequest || this.snippet || !('start' in this.row.hit)) return;

			ga('send', 'event', 'results', 'snippet/load', this.row.doc.docPid);

			const transformSnippets = UIStore.getState().results.shared.transformSnippets;
			const addons = UIStore.getState().results.hits.addons;
			const formatError = UIStore.getState().global.errorMessage;
			const concordanceSize = UIStore.getState().results.shared.concordanceSize;

			const nonce = this.row.hit;
			this.snippetRequest = Api.blacklab
			.getSnippet(INDEX_ID, this.row.doc.docPid, this.row.annotatedField?.id, this.row.hit.start, this.row.hit.end, concordanceSize)
			.then(s => {
				if (nonce !== this.row.hit) return; // hit has changed in the meantime.

				transformSnippets?.(s);

				// HACK! copy the colors from the existing hit. There's no easy way to get the entire Results object here to get the colors from there.
				// At least there's never be more highlights in the surrounding snippet than in the hit itself, so this works...
				const highlightColors = [...this.row.context.before, ...this.row.context.match, ...this.row.context.after]
					.reduce<Record<string, TokenHighlight>>((acc, t) => {
						t.captureAndRelation?.forEach(c => acc[c.highlight.key] = c.highlight);
						return acc;
					}, {});

				this.snippet = snippetParts(
					// @ts-ignore matchinfos not included in snippets. copy from the original hit.
					{matchInfos: this.row.hit.matchInfos,...s},
					highlightColors
				);

				// Run plugins defined for this corpus (e.g. a copy to clipboard button, or an audio player/text to speech button)
				this.addons = addons
					.map((a, i) => {
						try {
							return a({
								docId: this.row.doc.docPid,
								corpus: INDEX_ID,
								document: this.row.doc.docInfo,
								documentUrl: this.row.href || '',
								wordAnnotationId: this.info.mainAnnotation.id,
								dir: this.row.dir,
								citation: s
							});
						} catch (e) {
							console.error(e);
							return {
								name: 'error-' + i,
								content: `<pre class="text-danger">Error in addon: ${e}</pre>`
							}
						}
					})
					.filter(a => a != null);

				this.snippetRequest = null;
			})
			.catch((err: Api.ApiError) => {
				if (nonce !== this.row.hit) return; // hit has changed in the meantime.
				this.error = formatError(err, 'snippet');
				this.snippetRequest = null;
				if (err.stack) debugLog(err.stack);
				ga('send', 'exception', { exDescription: err.message, exFatal: false });
			})
		}
	},
	watch: {
		open: {
			immediate: true,
			handler() { if (this.open) this.loadSnippet(); }
		},
		sentenceShown: {
			immediate: true,
			handler() { if (this.sentenceShown) this.loadSentence(); }
		},
		data() {
			// Clear any data that's no longer relevant.
			this.snippetRequest = this.snippet = this.sentenceRequest = this.sentence = this.error = null;
			this.addons = [];
			this.sentenceShown = false;
		}
	},
});
